/**
 * useNotifications Hook
 * 
 * Manages browser notifications with permissions and scheduling
 * Handles alarm times and reminder intervals for tasks
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Task } from '../types';

interface NotificationOptions {
  title: string;
  body?: string;
  icon?: string;
  tag?: string;
  requireInteraction?: boolean;
}

export const useNotifications = () => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [supported, setSupported] = useState(false);
  const notificationTimeoutsRef = useRef<Map<string, number>>(new Map());
  const reminderIntervalsRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    // Check if notifications are supported
    if ('Notification' in window) {
      setSupported(true);
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!supported) {
      console.warn('Notifications are not supported in this browser');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, [supported]);

  const sendNotification = useCallback((options: NotificationOptions) => {
    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return null;
    }

    try {
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/vite.svg',
        tag: options.tag,
        requireInteraction: options.requireInteraction || false,
        badge: '/vite.svg',
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      return notification;
    } catch (error) {
      console.error('Error sending notification:', error);
      return null;
    }
  }, [permission]);

  const scheduleTaskAlarm = useCallback((task: Task) => {
    if (!task.alarm_time) return;

    const alarmTime = new Date(task.alarm_time);
    const now = new Date();
    const timeUntilAlarm = alarmTime.getTime() - now.getTime();

    // Clear existing alarm for this task
    const existingTimeout = notificationTimeoutsRef.current.get(task.id);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // If alarm is in the past, skip
    if (timeUntilAlarm <= 0) {
      console.log(`Alarm for task "${task.title}" is in the past`);
      return;
    }

    // Schedule the alarm
    const timeoutId = setTimeout(() => {
      sendNotification({
        title: '⏰ Task Alarm!',
        body: `${task.title}${task.description ? '\n' + task.description : ''}`,
        tag: `alarm-${task.id}`,
        requireInteraction: true,
      });

      // Play sound if available (optional)
      const audio = new Audio('/notification.mp3');
      audio.play().catch(() => {
        // Ignore if sound file doesn't exist
      });
    }, timeUntilAlarm);

    notificationTimeoutsRef.current.set(task.id, timeoutId);
    console.log(`Scheduled alarm for task "${task.title}" at ${alarmTime.toLocaleString()}`);
  }, [sendNotification]);

  const scheduleTaskReminders = useCallback((task: Task) => {
    if (!task.reminder_interval || task.status === 'completed') return;

    const intervalMinutes = task.reminder_interval;
    const intervalMs = intervalMinutes * 60 * 1000;

    // Clear existing reminder for this task
    const existingInterval = reminderIntervalsRef.current.get(task.id);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    // Set up recurring reminder
    const intervalId = setInterval(() => {
      // Don't send reminders for completed tasks
      if (task.status === 'completed') {
        clearInterval(intervalId);
        reminderIntervalsRef.current.delete(task.id);
        return;
      }

      sendNotification({
        title: 'Task Reminder',
        body: `Don't forget: ${task.title}`,
        tag: `reminder-${task.id}`,
        requireInteraction: false,
      });
    }, intervalMs);

    reminderIntervalsRef.current.set(task.id, intervalId);
    console.log(`Scheduled reminders for task "${task.title}" every ${intervalMinutes} minutes`);
  }, [sendNotification]);

  const clearTaskNotifications = useCallback((taskId: string) => {
    // Clear alarm
    const timeout = notificationTimeoutsRef.current.get(taskId);
    if (timeout) {
      clearTimeout(timeout);
      notificationTimeoutsRef.current.delete(taskId);
    }

    // Clear reminder
    const interval = reminderIntervalsRef.current.get(taskId);
    if (interval) {
      clearInterval(interval);
      reminderIntervalsRef.current.delete(taskId);
    }
  }, []);

  const scheduleAllTaskNotifications = useCallback((tasks: Task[]) => {
    // Clear all existing notifications first
    notificationTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    reminderIntervalsRef.current.forEach((interval) => clearInterval(interval));
    notificationTimeoutsRef.current.clear();
    reminderIntervalsRef.current.clear();

    // Schedule new notifications for all tasks
    tasks.forEach((task) => {
      if (task.alarm_time) {
        scheduleTaskAlarm(task);
      }
      if (task.reminder_interval && task.status !== 'completed') {
        scheduleTaskReminders(task);
      }
    });
  }, [scheduleTaskAlarm, scheduleTaskReminders]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      notificationTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      reminderIntervalsRef.current.forEach((interval) => clearInterval(interval));
    };
  }, []);

  return {
    supported,
    permission,
    requestPermission,
    sendNotification,
    scheduleTaskAlarm,
    scheduleTaskReminders,
    clearTaskNotifications,
    scheduleAllTaskNotifications,
  };
};
